'use client'

/**
 * GlobalSearch — the navbar search. An inline, always-visible input that expands a results dropdown
 * ANCHORED directly beneath it: no centered modal, no full-page dim/blur, no scroll lock. Fuzzy search
 * across three sources at once: PAGES (the nav-derived index, src/lib/search/pageIndex), TICKERS
 * (debounced /api/ticker-search), and ACTIONS (quick jumps). Keyboard-driven (up/down/enter/esc), recent
 * DESTINATIONS when empty, and a no-dead-end fallback that opens any typed text as a ticker. Opens on
 * focus or click, or via ⌘K / Ctrl+K / "/" from anywhere.
 *
 * Rendered once per layout (desktop + mobile navbar); only the VISIBLE instance answers the global
 * shortcuts (offsetParent check), and the results list mounts only while open, so it is cheap at rest.
 * Replaced the centered CommandPalette modal 2026-10-14 (owner: "stay there, extend out, not a popout").
 * ⚠ NO EM DASHES in user-facing copy.
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useRouter } from 'next/navigation'
import { Search, X, Clock, CornerDownLeft, Bell, BellRing, Star, CandlestickChart } from 'lucide-react'
import { searchPages, scorePage, PAGE_INDEX, type SearchablePage } from '@/lib/search/pageIndex'
import TickerLogo from '@/components/TickerLogo'

type Hit = { symbol: string; name: string | null }
const RECENTS_KEY = 'dt_cmdk_recent_v2'
const LEGACY_RECENTS_KEY = 'dt_search_recent'

/** A recent destination: a ticker you opened, or a page you navigated to (label kept so it renders even
 *  if the page ever leaves the index). */
type Recent = { k: 't'; sym: string } | { k: 'p'; href: string; label: string }

/** Load recents (v2 objects), migrating ONCE from the legacy ticker-only string[] so a returning user
 *  keeps their history. Client-only (localStorage); returns [] on any parse trouble. */
function loadRecents(): Recent[] {
  try {
    const rawV2 = localStorage.getItem(RECENTS_KEY)
    if (rawV2) {
      const arr = JSON.parse(rawV2)
      if (Array.isArray(arr)) return arr.filter((x): x is Recent =>
        !!x && ((x.k === 't' && typeof x.sym === 'string') || (x.k === 'p' && typeof x.href === 'string'))).slice(0, 6)
    }
    const legacy = JSON.parse(localStorage.getItem(LEGACY_RECENTS_KEY) || '[]')
    if (Array.isArray(legacy)) return legacy
      .filter((s: unknown): s is string => typeof s === 'string')
      .map((sym): Recent => ({ k: 't', sym })).slice(0, 6)
  } catch { /* ignore */ }
  return []
}

const ACTIONS: { label: string; href: string; Icon: typeof Bell; hay: string }[] = [
  { label: 'Set a price alert', href: '/alerts/price', Icon: BellRing, hay: 'set a price alert notify target price hit action' },
  { label: 'Go to my alerts', href: '/alerts', Icon: Bell, hay: 'go to my alerts feed live action' },
  { label: 'Open my watchlist', href: '/watchlist', Icon: Star, hay: 'open my watchlist tracked list action' },
  { label: 'Open a live chart', href: '/chart', Icon: CandlestickChart, hay: 'open a live chart charts candles action' },
]

/** Empty-state "Jump to" launcher: the high-intent destinations a trader opens first. Resolved against
 *  PAGE_INDEX so labels/icons never drift; any href missing from the index is dropped; keep the first six. */
const SUGGESTED: SearchablePage[] = [
  '/top-stocks', '/rvol-scanner', '/gap-scanner', '/biggest-gainers-today',
  '/premarket-movers', '/short-squeeze-stocks', '/most-active-stocks', '/money-map',
]
  .map((h) => PAGE_INDEX.find((p) => p.href === h))
  .filter((p): p is SearchablePage => !!p)
  .slice(0, 6)

type Row =
  | { kind: 'ticker'; group: string; hit: Hit }
  | { kind: 'page'; group: string; page: SearchablePage }
  | { kind: 'action'; group: 'Actions'; action: (typeof ACTIONS)[number] }
  | { kind: 'raw'; group: 'Open as ticker'; sym: string }

export default function GlobalSearch() {
  const router = useRouter()
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const [hits, setHits] = useState<Hit[]>([])
  const [active, setActive] = useState(0)
  const [recents, setRecents] = useState<Recent[]>([])
  const inputRef = useRef<HTMLInputElement | null>(null)
  const rootRef = useRef<HTMLDivElement | null>(null)
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)
  const panelRef = useRef<HTMLDivElement | null>(null)

  /** Focus this instance's input only when it is actually on screen (the other navbar copy is display:none
   *  at this breakpoint), so a global shortcut lands on the one the user can see. */
  const focusIfVisible = useCallback(() => {
    const el = inputRef.current
    if (el && el.offsetParent !== null) { el.focus(); return true }
    return false
  }, [])

  /** Measure where the dropdown should sit. It renders in a body-level portal (below) to escape the
   *  header's z-30 stacking context, so its position is computed here instead of via left-0/right-0.
   *  Anchored under the box, min 360px wide (so mobile labels are not clipped), clamped to the viewport. */
  const computePos = useCallback(() => {
    const el = rootRef.current
    if (!el) return
    const r = el.getBoundingClientRect()
    const vw = window.innerWidth
    const width = Math.min(Math.max(r.width, 360), vw - 16)
    let left = r.left
    if (left + width > vw - 8) left = vw - width - 8
    if (left < 8) left = 8
    setPos({ top: Math.round(r.bottom + 8), left: Math.round(left), width: Math.round(width) })
  }, [])

  // Global shortcuts: ⌘K / Ctrl+K toggles, "/" focuses (never while typing elsewhere), and the legacy
  // open-command-palette event still focuses, so any old caller keeps working.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        if (inputRef.current?.offsetParent == null) return
        e.preventDefault()
        if (document.activeElement === inputRef.current) { setOpen(false); inputRef.current?.blur() }
        else focusIfVisible()
      } else if (e.key === '/') {
        const a = document.activeElement as HTMLElement | null
        if (a && (a.tagName === 'INPUT' || a.tagName === 'TEXTAREA' || a.isContentEditable)) return
        if (inputRef.current?.offsetParent == null) return
        e.preventDefault(); focusIfVisible()
      }
    }
    const onOpenEvt = () => { focusIfVisible() }
    window.addEventListener('keydown', onKey)
    window.addEventListener('open-command-palette', onOpenEvt)
    return () => { window.removeEventListener('keydown', onKey); window.removeEventListener('open-command-palette', onOpenEvt) }
  }, [focusIfVisible])

  // Load recents each time it opens (cheap, keeps them fresh after a navigation).
  useEffect(() => { if (open) setRecents(loadRecents()) }, [open])

  // Body-level fixed layer: paints ABOVE the chat dock (z-9999) and watchlist pane (z-9998) rather than
  // under them, and widens past the narrow mobile box. The navbar is fixed, so no scroll listener is
  // needed; recompute on resize only.
  useEffect(() => {
    if (!open) { setPos(null); return }
    computePos()
    window.addEventListener('resize', computePos)
    return () => window.removeEventListener('resize', computePos)
  }, [open, computePos])

  // Close on a pointer press outside BOTH the box and the (portaled) dropdown. Row and clear mousedown
  // are preventDefault-ed below, so selecting a result never triggers this.
  useEffect(() => {
    if (!open) return
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node
      if (rootRef.current?.contains(t) || panelRef.current?.contains(t)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', onDown)
    return () => document.removeEventListener('mousedown', onDown)
  }, [open])

  // Debounced ticker search, only while open with a term.
  useEffect(() => {
    const t0 = q.trim()
    if (!open || !t0) { setHits([]); return }
    let alive = true
    const t = setTimeout(() => {
      fetch(`/api/ticker-search?q=${encodeURIComponent(t0)}`)
        .then((r) => (r.ok ? r.json() : null))
        .then((j: { results?: Hit[] } | null) => { if (alive) setHits(j?.results ?? []) })
        .catch(() => {})
    }, 130)
    return () => { alive = false; clearTimeout(t) }
  }, [q, open])

  useEffect(() => { setActive(0) }, [q])

  const term = q.trim()
  const pages = useMemo(() => (term ? searchPages(term, 7) : []), [term])
  const actions = useMemo(() => {
    if (!term) return []
    return ACTIONS
      .map((a) => ({ a, s: scorePage(term, { href: a.href, label: a.label, Icon: a.Icon, group: 'Actions', hay: a.hay }) }))
      .filter((x) => x.s > -Infinity).sort((x, y) => y.s - x.s).slice(0, 3).map((x) => x.a)
  }, [term])

  const rows: Row[] = useMemo(() => {
    const r: Row[] = []
    if (!term) {
      const seen = new Set<string>()
      recents.forEach((rec) => {
        if (rec.k === 't') { r.push({ kind: 'ticker', group: 'Recent', hit: { symbol: rec.sym, name: null } }) }
        else {
          seen.add(rec.href)
          const p = PAGE_INDEX.find((x) => x.href === rec.href) ?? { href: rec.href, label: rec.label, Icon: Clock, group: 'Recent', hay: '' }
          r.push({ kind: 'page', group: 'Recent', page: p })
        }
      })
      SUGGESTED.filter((p) => !seen.has(p.href)).forEach((page) => r.push({ kind: 'page', group: 'Jump to', page }))
      return r
    }
    hits.forEach((hit) => r.push({ kind: 'ticker', group: 'Tickers', hit }))
    pages.forEach((page) => r.push({ kind: 'page', group: 'Pages', page }))
    actions.forEach((action) => r.push({ kind: 'action', group: 'Actions', action }))
    if (r.length === 0) {
      // Only offer "Open as ticker" when the term LOOKS like a ticker (1-5 letters, optional .X class),
      // so a garbage search does not ship a dead-end row that 404s on /stocks/<garbage>.
      const sym = term.toUpperCase().replace(/[^A-Z.]/g, '')
      if (/^[A-Z]{1,5}(\.[A-Z]{1,2})?$/.test(sym)) r.push({ kind: 'raw', group: 'Open as ticker', sym })
    }
    return r
  }, [hits, pages, actions, term, recents])

  const remember = useCallback((rec: Recent) => {
    try {
      const idOf = (r: Recent) => (r.k === 't' ? 't:' + r.sym : 'p:' + r.href)
      const n = [rec, ...recents.filter((r) => idOf(r) !== idOf(rec))].slice(0, 6)
      localStorage.setItem(RECENTS_KEY, JSON.stringify(n)); setRecents(n)
    } catch { /* ignore */ }
  }, [recents])

  const close = useCallback(() => { setOpen(false); inputRef.current?.blur() }, [])
  const go = useCallback((href: string, label?: string) => { if (label) remember({ k: 'p', href, label }); close(); router.push(href) }, [remember, router, close])
  const goStock = useCallback((sym: string) => { if (!sym) return; remember({ k: 't', sym }); close(); router.push(`/stocks/${encodeURIComponent(sym)}`) }, [remember, router, close])

  const select = useCallback((row: Row | undefined) => {
    if (!row) return
    if (row.kind === 'ticker') goStock(row.hit.symbol)
    else if (row.kind === 'page') go(row.page.href, row.page.label)
    else if (row.kind === 'action') go(row.action.href, row.action.label)
    else goStock(row.sym)
  }, [go, goStock])

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(a + 1, rows.length - 1)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)) }
    else if (e.key === 'Enter') { e.preventDefault(); select(rows[active]) }
    else if (e.key === 'Escape') { e.preventDefault(); if (q) setQ(''); else close() }
    else if (e.key === 'Tab') { setOpen(false) } // let focus move on naturally, just close the dropdown
  }

  const activeRow = (i: number) => i === active

  return (
    <div ref={rootRef} className="relative w-full">
      <div
        onMouseDown={(e) => { if (e.target !== inputRef.current) { e.preventDefault(); inputRef.current?.focus() } }}
        className={`flex w-full cursor-text items-center gap-2 rounded-lg border bg-card/60 px-3 py-1.5 transition-colors ${open ? 'border-amber-500/60 bg-card/90 shadow-[0_0_0_3px_rgba(245,158,11,0.12)]' : 'border-border/60 hover:border-amber-500/50'}`}
      >
        <Search className={`h-4 w-4 shrink-0 transition-colors ${open ? 'text-amber-400' : 'text-muted-foreground'}`} />
        <input
          ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
          onFocus={() => setOpen(true)}
          placeholder="Search tickers or pages…" aria-label="Search tickers, pages and actions"
          aria-expanded={open} aria-controls="global-search-results" role="combobox" aria-autocomplete="list"
          autoComplete="off" spellCheck={false}
          className="min-w-0 flex-1 bg-transparent text-sm font-medium text-foreground placeholder-muted-foreground focus:outline-none"
        />
        {q
          ? <button type="button" aria-label="Clear" onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(''); inputRef.current?.focus() }} className="flex-none text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>
          : <kbd className="hidden shrink-0 rounded border border-border border-b-2 bg-card px-1.5 py-0.5 font-mono text-[10px] font-semibold text-muted-foreground lg:inline">⌘K</kbd>}
      </div>

      {open && pos && typeof document !== 'undefined' && createPortal(
        <div
          ref={panelRef}
          id="global-search-results" role="listbox" aria-label="Search results"
          style={{ position: 'fixed', top: pos.top, left: pos.left, width: pos.width, zIndex: 10000 }}
          className="gs-pop overflow-hidden rounded-2xl border border-border/70 bg-popover dark:bg-gradient-to-b dark:from-zinc-900 dark:to-[#0d0f16] shadow-2xl shadow-black/60"
        >
          <style>{'@keyframes gspop{from{opacity:0;transform:translateY(-6px) scale(.99)}to{opacity:1;transform:none}}.gs-pop{animation:gspop .16s cubic-bezier(.2,.7,.3,1)}@media (prefers-reduced-motion:reduce){.gs-pop{animation:none}}'}</style>
          <div className="max-h-[min(62vh,440px)] overflow-y-auto py-2">
            {rows.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted-foreground">No matches. Try a ticker like <span className="font-semibold text-foreground">NVDA</span> or a page name.</div>
            ) : rows.map((row, i) => {
              const header = i === 0 || rows[i - 1].group !== row.group
                ? <div className="px-4 pb-1 pt-3 text-[10.5px] font-bold uppercase tracking-[0.13em] text-muted-foreground">{row.group}</div>
                : null
              const isTickerLike = row.kind === 'ticker' || row.kind === 'raw'
              const sym = row.kind === 'ticker' ? row.hit.symbol : row.kind === 'raw' ? row.sym : ''
              const Icon = row.kind === 'page' ? row.page.Icon : row.kind === 'action' ? row.action.Icon : null
              const label = row.kind === 'ticker' ? '$' + row.hit.symbol : row.kind === 'raw' ? '$' + row.sym : row.kind === 'page' ? row.page.label : row.action.label
              const sub = row.kind === 'ticker' ? (row.hit.name || undefined) : row.kind === 'raw' ? 'chart, alerts & RVOL history' : undefined
              const tag = row.kind === 'ticker' ? 'Ticker' : row.kind === 'raw' ? 'Enter' : row.kind === 'page' ? row.page.group : 'Action'
              return (
                <Fragment key={row.kind + i + (sym || (row.kind === 'page' ? row.page.href : row.kind === 'action' ? row.action.href : ''))}>
                  {header}
                  <button type="button" role="option" aria-selected={activeRow(i)} onMouseDown={(e) => e.preventDefault()}
                    onMouseMove={() => setActive(i)} onClick={() => select(row)}
                    className={`group mx-2 flex w-[calc(100%-16px)] items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors ${activeRow(i) ? 'bg-amber-500/10 ring-1 ring-inset ring-amber-500/30' : 'hover:bg-white/[0.03]'}`}>
                    <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg border border-border bg-card/70 text-foreground">
                      {isTickerLike
                        ? <TickerLogo symbol={sym} size={20} rounded="rounded" />
                        : Icon ? <Icon className="h-4 w-4" /> : null}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-[14.5px] font-semibold text-foreground">{label}</span>
                      {sub && <span className="block truncate text-[12px] text-muted-foreground">{sub}</span>}
                    </span>
                    <span className="hidden flex-none rounded-full border border-border px-2.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-muted-foreground md:inline-block">{tag}</span>
                    <CornerDownLeft className={`hidden h-3.5 w-3.5 flex-none md:block ${activeRow(i) ? 'text-amber-400' : 'text-transparent'}`} />
                  </button>
                </Fragment>
              )
            })}
          </div>

          <div className="flex items-center gap-4 border-t border-border/70 bg-background/60 px-4 py-2.5 text-[11.5px] text-muted-foreground">
            <span className="hidden items-center gap-1.5 sm:inline-flex"><kbd className="rounded border border-border px-1.5 font-mono text-[10px] text-muted-foreground">↑</kbd><kbd className="rounded border border-border px-1.5 font-mono text-[10px] text-muted-foreground">↓</kbd> navigate</span>
            <span className="inline-flex items-center gap-1.5"><kbd className="rounded border border-border px-1.5 font-mono text-[10px] text-muted-foreground">↵</kbd> open</span>
            <span className="ml-auto font-mono text-muted-foreground"><span className="font-semibold text-amber-500">⌘K</span> from anywhere</span>
          </div>
        </div>,
        document.body,
      )}
    </div>
  )
}
